import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Collapse,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  AutoAwesome as AIIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  VolumeUp as AudioIcon,
  Description as DocumentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Restaurant as RestaurantIcon,
  AttachMoney as PriceIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

import { useGenerateAnswersMutation } from "../../redux/thunks/ai/generateanswers";

const BusinessQASection = ({ 
  businessName = '', 
  businessId = '', 
  hasDocuments = false,
  hasAudio = false,
  documentTypes = [],
  isPreview = false, 
  isBusinessOwner = false
}) => {
  const navigate = useNavigate();
  const questionsEndRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Redux selectors
  const token = useSelector((state) => state.auth?.token || null);
  const displayName = useSelector((state) => state.auth?.user?.displayName || 'User');
  const userId = useSelector((state) => state.auth?.userId || null);

  // Local state
  const [query, setQuery] = useState('');
  const [questions, setQuestions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [clearDialog, setClearDialog] = useState(false);
  const [expanded, setExpanded] = useState(!isMobile); // Auto-collapse on mobile

  const [generateAnswer, { isLoading }] = useGenerateAnswersMutation();

  // Enhanced capabilities detection
  const hasAICapabilities = hasDocuments || hasAudio;
  const isFullyFunctional = hasAICapabilities && !isPreview;

  // Memoized sample queries - Enhanced for different scenarios
  const sampleQueries = useMemo(() => {
    if (hasDocuments && documentTypes.length > 0) {
      // Intelligent queries based on document types
      const queries = [];
      if (documentTypes.includes('menu')) {
        queries.push("What's on your menu?", "What are your prices?");
      }
      if (documentTypes.includes('services')) {
        queries.push("What services do you offer?", "How much do you charge?");
      }
      if (documentTypes.includes('policy')) {
        queries.push("What's your policy?", "What are your terms?");
      }
      // Add generic ones if we have room
      if (queries.length < 4) {
        queries.push("What makes you special?", "Do you have specials?");
      }
      return queries.slice(0, 4);
    }
    
    if (hasAudio) {
      return [
        "Tell me about your business",
        "What makes you unique?",
        "What are your specialties?", 
        "How can I contact you?"
      ];
    }

    // Default queries when no AI capabilities
    return [
      "What are your hours?",
      "Do you offer delivery?",
      "What makes you special?",
      "How can I contact you?"
    ];
  }, [hasDocuments, hasAudio, documentTypes]);

  // Handle query submission
  const handleSubmit = useCallback(async () => {
    if (!token) {
      navigate('/auth/signin', { 
        state: { 
          name: 'businessDetails', 
          link: `/business/${businessId}/${encodeURIComponent(businessName.replace(/[\s,\.]+/g, '-').toLowerCase())}` 
        } 
      });
      return;
    }

    if (!query.trim() || isLoading) return;
    
    const newQuestion = {
      id: Date.now(),
      question: query.trim(),
      timestamp: new Date().toISOString(),
      status: "pending",
      userName: displayName,
      userId: userId
    };

    setQuestions(prev => [newQuestion, ...prev]);
    const currentQuery = query.trim();
    setQuery('');
    
    // Auto-show history when question is submitted
    setShowHistory(true);
    
    try {
      const response = await generateAnswer({ 
        payload: {
          auth: { token, authType: "token" },
          businessId,
          query: currentQuery,
        }
      }).unwrap();
      
      setQuestions(prev => prev.map(q => 
        q.id === newQuestion.id 
          ? { 
              ...q, 
              answer: response.text,
              sources: response.sources || [],
              confidence: response.confidence || 0.9,
              status: "answered"
            }
          : q
      ));
      
    } catch (error) {
      console.error("Error:", error);
      setQuestions(prev => prev.map(q => 
        q.id === newQuestion.id 
          ? { 
              ...q, 
              answer: isFullyFunctional 
                ? "Sorry, I couldn't process your question right now. Please try again." 
                : "This business hasn't set up AI-powered responses yet. Contact them directly for information.",
              status: "error"
            }
          : q
      ));
    }
  }, [token, query, isLoading, navigate, businessId, businessName, displayName, userId, generateAnswer, isFullyFunctional]);

  // Handle sample query click
  const handleSampleClick = useCallback((sample) => {
    setQuery(sample);
    if (isMobile) {
      // On mobile, auto-focus the text field after setting query
      setTimeout(() => {
        const textField = document.querySelector('input[placeholder*="Ask"]');
        if (textField) {
          textField.focus();
        }
      }, 100);
    }
  }, [isMobile]);

  // Clear query
  const handleClearQuery = useCallback(() => {
    setQuery('');
  }, []);

  // ALWAYS RENDER - Remove the early return condition
  return (
    <Box sx={{ mb: 2 }}>
      {/* Mobile-friendly collapsible container */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          overflow: 'hidden', // Prevent content overflow
        }}
      >
        {/* Header - Mobile optimized */}
        <Box sx={{ 
          p: isMobile ? 2 : 3,
          pb: isMobile && !expanded ? 2 : isMobile ? 1 : 3,
          borderBottom: isMobile && expanded ? '1px solid' : 'none',
          borderBottomColor: 'divider'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            minHeight: isMobile ? '40px' : '48px'
          }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                flex: 1,
                cursor: isMobile ? 'pointer' : 'default'
              }}
              onClick={() => isMobile && setExpanded(!expanded)}
            >
              <AIIcon color="primary" sx={{ fontSize: isMobile ? 20 : 24 }} />
              <Typography variant={isMobile ? "subtitle1" : "h6"} component="h2">
                Ask {isMobile ? '' : businessName}
              </Typography>
              
              {/* Status indicators */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {isPreview && (
                  <Chip label="PREVIEW" size="small" color="info" variant="outlined" />
                )}
               
              </Box>
            </Box>
            
            {/* Mobile toggle and clear button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {questions.length > 0 && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setClearDialog(true)}
                  title="Clear all questions"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
              
              {isMobile && (
                <IconButton onClick={() => setExpanded(!expanded)} size="small">
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
            </Box>
          </Box>
          
          {/* Mobile subtitle when collapsed */}
          {isMobile && !expanded && (
            <Typography variant="caption" color="text.secondary">
              Tap to ask questions about {businessName}
            </Typography>
          )}
        </Box>

        {/* Collapsible content */}
        <Collapse in={!isMobile || expanded}>
          <Box sx={{ p: isMobile ? 2 : 3, pt: isMobile ? 2 : 0 }}>
            
            {/* Enhanced description based on capabilities */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isFullyFunctional 
                ? `Ask natural language questions about our ${hasDocuments ? 'menu, services, prices' : 'offerings'} and more.`
                : hasAICapabilities 
                  ? "AI-powered questions will be available once this business completes setup."
                  : "Ask questions about this business. Enhanced AI responses available when business uploads content."
              }
              {!token && " Sign in to ask questions."}
            </Typography>

            {/* Enhanced preview/setup notices */}
            {isPreview && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Preview Mode:</strong> This AI feature will be fully functional once you upload business documents or audio descriptions.
                </Typography>
              </Alert>
            )}
            
            {isBusinessOwner && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  💡 <strong>Business Owner?</strong> Upload documents or record an audio description to enable advanced AI-powered responses about your business.
                </Typography>
              </Alert>
            )}

            {/* Mobile-optimized Query Input */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ 
                display: 'flex',
                gap: isMobile ? 0.5 : 1,
                alignItems: 'stretch',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 0.5,
                  flex: 1
                }}>
                  <TextField
                    fullWidth
                    placeholder={token 
                      ? `Ask anything about ${businessName}...` 
                      : "Sign in to ask questions..."
                    }
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit();
                      }
                    }}
                    disabled={isLoading}
                    size={isMobile ? "medium" : "small"}
                    multiline={isMobile}
                    rows={isMobile ? 2 : 1}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                      endAdornment: query && (
                        <IconButton 
                          size="small" 
                          onClick={handleClearQuery}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: isMobile ? 2 : 1,
                      }
                    }}
                  />
                </Box>
                
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!query.trim() || isLoading || !token}
                  size={isMobile ? "large" : "medium"}
                  sx={{ 
                    minWidth: isMobile ? '100%' : '100px',
                    height: isMobile ? '48px' : '40px',
                    borderRadius: isMobile ? 2 : 1,
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <>
                      <SendIcon sx={{ mr: isMobile ? 1 : 0 }} />
                      {isMobile && 'Ask AI'}
                    </>
                  )}
                </Button>
              </Box>
            </Box>

            {/* Mobile-friendly Sample Questions */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontSize: isMobile ? '0.875rem' : '0.875rem' }}>
                Try asking:
              </Typography>
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: isMobile ? 0.5 : 1
              }}>
                {sampleQueries.map((sample, index) => (
                  <Chip
                    key={index}
                    label={sample}
                    size={isMobile ? "medium" : "small"}
                    variant="outlined"
                    onClick={() => handleSampleClick(sample)}
                    disabled={!token}
                    sx={{ 
                      cursor: !token ? 'default' : 'pointer',
                      justifyContent: 'flex-start',
                      height: isMobile ? '36px' : 'auto',
                      '& .MuiChip-label': {
                        fontSize: isMobile ? '0.75rem' : '0.8125rem',
                        padding: isMobile ? '0 8px' : '0 12px'
                      },
                      '&:hover': {
                        backgroundColor: token ? 'action.hover' : 'transparent',
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Questions History Toggle - Mobile optimized */}
            {questions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setShowHistory(!showHistory)}
                  endIcon={showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  size={isMobile ? "large" : "medium"}
                  sx={{
                    height: isMobile ? '44px' : 'auto',
                    justifyContent: 'space-between',
                    textAlign: 'left'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant={isMobile ? "body1" : "body2"}>
                      {showHistory ? 'Hide' : 'Show'} Questions & Answers
                    </Typography>
                    <Chip 
                      label={questions.length} 
                      size="small" 
                      color="primary" 
                      variant="outlined" 
                    />
                  </Box>
                </Button>
              </Box>
            )}

            {/* Questions List - Mobile optimized */}
            <Collapse in={showHistory && questions.length > 0}>
              <Box
                sx={{
                  maxHeight: isMobile ? '400px' : '500px',
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'grey.50'
                }}
              >
                {questions.map((q, index) => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    isLast={index === questions.length - 1}
                    isLoading={isLoading && q.status === 'pending'}
                    isMobile={isMobile}
                    isFullyFunctional={isFullyFunctional}
                  />
                ))}
              </Box>
            </Collapse>

            {/* Attribution - Mobile friendly */}
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                mt: 2, 
                display: 'block', 
                textAlign: 'center',
                fontSize: isMobile ? '0.7rem' : '0.75rem'
              }}
            >
              Powered by ChatGPT 
            </Typography>
          </Box>
        </Collapse>
      </Box>

      {/* Clear Dialog */}
      <Dialog 
        open={clearDialog} 
        onClose={() => setClearDialog(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: isMobile ? { m: 0 } : {}
        }}
      >
        <DialogTitle>
          Clear All Questions
          {isMobile && (
            <IconButton
              onClick={() => setClearDialog(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <ClearIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear all {questions.length} questions and answers? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 3 : 1.5 }}>
          <Button 
            onClick={() => setClearDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? "large" : "medium"}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setQuestions([]);
              setClearDialog(false);
              setShowHistory(false);
            }} 
            color="error" 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? "large" : "medium"}
          >
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Enhanced Question Card Component - Mobile optimized
const QuestionCard = React.memo(({ question, isLast, isLoading, isMobile, isFullyFunctional }) => {
  const [expanded, setExpanded] = useState(!isMobile); // Auto-collapse answers on mobile

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        m: 1,
        bgcolor: 'background.paper',
        '&:last-child': { mb: 1 },
        boxShadow: 'none',
        border: isMobile ? '1px solid' : '1px solid',
        borderColor: 'divider'
      }}
    >
      <CardContent sx={{ p: isMobile ? 2 : 2, pb: 1 }}>
        {/* Question Header - Mobile optimized */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          gap: 1, 
          mb: 1,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <PersonIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" color="primary">
              {question.userName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(question.timestamp).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
          </Box>
        </Box>

        {/* Question Text - Mobile friendly */}
        <Typography 
          variant="body1" 
          sx={{ 
            fontWeight: 500, 
            mb: 2,
            fontSize: isMobile ? '0.95rem' : '1rem',
            lineHeight: isMobile ? 1.4 : 1.5
          }}
        >
          {question.question}
        </Typography>

        {/* Answer Section - Enhanced for mobile */}
        {question.status === "pending" && isLoading ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            p: isMobile ? 1.5 : 2, 
            bgcolor: 'grey.100', 
            borderRadius: 2
          }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              AI is thinking...
            </Typography>
          </Box>
        ) : question.answer ? (
          <Box>
            {/* Mobile: Collapsible answer */}
            {isMobile && (
              <Box sx={{ mb: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                  endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ p: 0, minHeight: 'auto' }}
                >
                  {expanded ? 'Hide' : 'Show'} Answer
                </Button>
              </Box>
            )}
            
            <Collapse in={!isMobile || expanded}>
              <Box
                sx={{ 
                  p: isMobile ? 1.5 : 2, 
                  bgcolor: question.status === 'error' ? 'error.light' : 'primary.light',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: question.status === 'error' ? 'error.main' : 'primary.main'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <AIIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2">AI Response</Typography>
                  {question.confidence && question.status !== 'error' && (
                    <Chip 
                      label={`${Math.round(question.confidence * 100)}% confident`} 
                      size="small" 
                      color="success" 
                      variant="outlined" 
                    />
                  )}
                  
                </Box>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'primary.contrastText',
                    fontSize: isMobile ? '0.85rem' : '0.875rem',
                    lineHeight: isMobile ? 1.4 : 1.5
                  }}
                >
                  {question.answer}
                </Typography>
                
                {question.sources && question.sources.length > 0 && (
                  <Box sx={{ 
                    mt: 1, 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 0.5,
                    alignItems: 'center'
                  }}>
                    <Typography variant="caption" sx={{ mr: 1, fontWeight: 500 }}>
                      Sources:
                    </Typography>
                    {question.sources.map((source, index) => (
                      <Chip
                        key={index}
                        label={source}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        ) : question.status === "error" ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            Sorry, there was an error processing your question.
          </Alert>
        ) : (
          <Typography variant="body2" color="warning.main">
            Processing...
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});

QuestionCard.displayName = 'QuestionCard';

export default BusinessQASection;